const OIDC_CALLBACK_PARAMETERS = new Set([
    'code',
    'state',
    'error',
    'error_description',
    'id_token',
    'access_token',
    'nonce',
    'session_state',
]);

const OIDC_PRIMARY_RESPONSE_PARAMETERS = new Set([
    'code',
    'error',
    'id_token',
    'access_token',
]);

const DIRECT_BACKOFFICE_ROUTE_ROOTS = [
    '/ai',
    '/connect',
    '/connections',
    '/domains',
    '/enrich',
    '/products',
    '/translations',
    '/webinars',
];

function hasOidcPrimaryResponseParameter(parameters: URLSearchParams): boolean {
    let containsPrimaryResponseParameter = false;
    parameters.forEach((_, parameter) => {
        containsPrimaryResponseParameter ||= OIDC_PRIMARY_RESPONSE_PARAMETERS.has(parameter);
    });
    return containsPrimaryResponseParameter;
}

function hasOidcCallbackParameter(parameters: URLSearchParams): boolean {
    let containsCallbackParameter = false;
    parameters.forEach((_, parameter) => {
        containsCallbackParameter ||= OIDC_CALLBACK_PARAMETERS.has(parameter);
    });
    return containsCallbackParameter;
}

function hasOidcPrimaryResponseHashParameter(hash: string): boolean {
    return hasOidcPrimaryResponseParameter(getHashParameters(hash));
}

function hasOidcCallbackHashParameter(hash: string): boolean {
    return hasOidcCallbackParameter(getHashParameters(hash));
}

function getHashParameters(hash: string): URLSearchParams {
    const hashContent = hash.slice(1);
    const query = hashContent.startsWith('?')
        ? hashContent.slice(1)
        : hashContent.includes('?')
            ? hashContent.slice(hashContent.indexOf('?') + 1)
            : hashContent;

    return new URLSearchParams(query);
}

/** True when the browser URL carries an OIDC redirect response in its query or fragment. */
export function hasOidcCallbackMaterial(browserUrl: string): boolean {
    try {
        const url = new URL(browserUrl, window.location.origin);
        return hasOidcPrimaryResponseParameter(url.searchParams) || hasOidcPrimaryResponseHashParameter(url.hash);
    } catch {
        return false;
    }
}

/**
 * Returns the allowed application target after an authentication redirect.
 * Callback parameters and callback fragments are never reused as application state.
 */
export function getSafeBackofficeReturnTarget(browserUrl: string): string | null {
    try {
        const url = new URL(browserUrl, window.location.origin);
        if (url.origin !== window.location.origin) {
            return null;
        }

        const path = url.pathname.replace(/\/+$/, '') || '/';
        if (path === '/' || path === '/auth/callback') {
            return null;
        }

        if (!DIRECT_BACKOFFICE_ROUTE_ROOTS.some(root => path === root || path.startsWith(`${root}/`))) {
            return null;
        }

        if (hasOidcCallbackMaterial(browserUrl)) {
            const oidcParameters: string[] = [];
            url.searchParams.forEach((_, parameter) => {
                if (OIDC_CALLBACK_PARAMETERS.has(parameter)) {
                    oidcParameters.push(parameter);
                }
            });
            oidcParameters.forEach(parameter => url.searchParams.delete(parameter));

            if (hasOidcCallbackHashParameter(url.hash)) {
                url.hash = '';
            }
        }

        return `${path}${url.search}${url.hash}`;
    } catch {
        return null;
    }
}
